import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TextFieldProps,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import { ContributionFormData } from '../../types/client';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { useContributions } from './hooks/useContributions';
import { Button, Placeholder } from '@/components/ui';

// Integer part grouped the Indian way (12,34,567); the decimal part kept as typed.
const groupIndian = (raw: string) => {
  const [int, dec] = raw.split('.');
  const grouped = int ? formatNumber(Number(int), 0) : dec !== undefined ? '0' : '';
  return dec !== undefined ? `${grouped}.${dec}` : grouped;
};

const parseAmount = (text: string) => Number(text.replace(/,/g, '')) || 0;

/** A money field that shows Indian digit grouping as you type, and reports a plain number. */
export const AmountInput: React.FC<
  Omit<TextFieldProps, 'value' | 'onChange'> & { value: number; onChange: (value: number) => void }
> = ({ value, onChange, inputProps, ...props }) => {
  const [text, setText] = useState(() => groupIndian(String(value ?? 0)));
  const inputRef = useRef<HTMLInputElement>(null);

  // Follow outside changes (load, rounding), but not while the text already means this
  // number — "1,500." must survive the keystroke that typed the dot.
  useEffect(() => {
    setText(current => (parseAmount(current) === value ? current : groupIndian(String(value ?? 0))));
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const typed = e.target.value;
    // Digits and the dot before the caret, so the caret stays put as commas move.
    const keptBeforeCaret = typed.slice(0, e.target.selectionStart ?? typed.length).replace(/[^\d.]/g, '').length;
    const [int, ...rest] = typed.replace(/[^\d.]/g, '').split('.');
    const raw = rest.length ? `${int}.${rest.join('').slice(0, 2)}` : int;
    const next = groupIndian(raw);
    setText(next);
    onChange(parseAmount(next));
    requestAnimationFrame(() => {
      let pos = 0;
      for (let kept = 0; pos < next.length && kept < keptBeforeCaret; pos++) {
        if (/[\d.]/.test(next[pos])) kept++;
      }
      inputRef.current?.setSelectionRange(pos, pos);
    });
  };

  return (
    <TextField
      {...props}
      value={text}
      onChange={handleChange}
      inputRef={inputRef}
      inputProps={{ inputMode: 'decimal', ...inputProps, style: { textAlign: 'right', ...inputProps?.style } }}
    />
  );
};

type SnackbarState =
  | { open: boolean; message: string; severity: 'success' | 'error' }
  | null;

interface ContributionsProps {
  client_id?: number;
  client_code: string;
  scheme_id: number;
  viewMode?: boolean;
  onModified?: () => void;
  previousContributions?: ContributionFormData[];
  data?: ContributionFormData[];
}

export interface ContributionsRef {
  getPendingChanges: () => ContributionFormData[];
  getDeletedContributions: () => number[];
}

const Contributions = forwardRef<ContributionsRef, ContributionsProps>(
  (
    {
      client_id,
      client_code,
      scheme_id,
      viewMode = false,
      onModified,
      previousContributions = [],
      data,
    },
    ref
  ) => {
    const [snackbar, setSnackbar] = useState<SnackbarState>(null);

    const {
      rows,
      loading,
      errors,
      deletedIds,
      isChanged,
      handleAdd,
      handleChange,
      commitDates,
      handleRemove,
      validate,
      refresh
    } = useContributions({
      clientId: client_id,
      clientCode: client_code,
      schemeId: scheme_id,
      viewMode,
      onModified,
      data,
      previousContributions
    });

    useImperativeHandle(ref, () => ({
      getPendingChanges: () => {
        if (!validate()) {
          setSnackbar({
            open: true,
            message: 'Fix validation errors in Contributions',
            severity: 'error',
          });
          throw new Error('Validation failed');
        }

        if (!isChanged) return [];

        // valid_to is derived by the backend; key and redemption_delta are table-only.
        return rows.map(({ key, redemption_delta, valid_to, ...r }) => ({
          ...r,
          scheme: Number(r.scheme),
          amount: Number(r.amount),
          top_up_amount: Number(r.top_up_amount),
          redemption_amount: Number(r.redemption_amount),
        }));
      },
      getDeletedContributions: () => deletedIds,
      refresh: () => {
        refresh();
      }
    }));

    if (loading) {
      return (
        <Placeholder kind="loading" />
      );
    }

    return (
      <Box sx={{ width: '100%', p: 1 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={700}>
            Contributions
          </Typography>
          <Stack direction="row" spacing={2}>
            {!viewMode && isChanged && (
              <Button
                size="sm"
                onClick={() => {
                  if (validate()) {
                    setSnackbar({
                      open: true,
                      message: 'Contributions staged successfully',
                      severity: 'success',
                    });
                  }
                }}
              >
                Local Save
              </Button>
            )}
            {!viewMode && (
              <Button
                size="sm"
                icon={<AddIcon fontSize="small" />}
                variant="primary"
                onClick={handleAdd}
              >
                Add Contribution
              </Button>
            )}
          </Stack>
        </Stack>

        <Card variant="outlined">
          <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
            <TableContainer>
              <Table
                sx={{
                  '& th, & td': {
                    borderRight: '1px solid var(--border)',
                    borderBottom: '1px solid var(--border)',
                  },
                  '& th:last-of-type, & td:last-of-type': { borderRight: 'none' },
                  '& tbody tr:last-of-type td': { borderBottom: 'none' },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Valid From</TableCell>
                    <TableCell align="right">Top Up</TableCell>
                    <TableCell align="right">Cumulative Top Up</TableCell>
                    <TableCell align="right">Redemption</TableCell>
                    <TableCell align="right">Cumulative Redemption</TableCell>
                    {!viewMode && <TableCell />}
                  </TableRow>
                </TableHead>
                <TableBody
                  sx={{
                    backgroundColor: isChanged && previousContributions.length > 0 ? 'var(--warn-soft)' : undefined,
                  }}
                >
                  {rows.map((r, i) => (
                    <TableRow key={r.key}>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={r.valid_from}
                          disabled={viewMode}
                          error={!!errors[i]?.valid_from}
                          helperText={errors[i]?.valid_from}
                          onChange={e =>
                            handleChange(i, 'valid_from', e.target.value)
                          }
                          onBlur={commitDates}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <AmountInput
                          size="small"
                          value={r.top_up_amount}
                          disabled={viewMode}
                          error={!!errors[i]?.top_up_amount}
                          helperText={errors[i]?.top_up_amount}
                          onChange={value => handleChange(i, 'top_up_amount', value)}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(r.amount, r.currency_code)}</TableCell>
                      <TableCell align="right">
                        <AmountInput
                          size="small"
                          value={r.redemption_delta}
                          disabled={viewMode}
                          error={!!errors[i]?.redemption_amount}
                          helperText={errors[i]?.redemption_amount}
                          onChange={value => handleChange(i, 'redemption_delta', value)}
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(r.redemption_amount, r.currency_code)}</TableCell>
                      {!viewMode && (
                        <TableCell align="left">
                          <Tooltip title="Remove">
                            <span>
                              <Button
                                iconOnly
                                aria-label="Remove"
                                onClick={() => handleRemove(i)}
                                style={{ color: 'var(--neg)' }}
                              >
                                <DeleteIcon fontSize="small" />
                              </Button>
                            </span>
                          </Tooltip>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {snackbar && (
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={() => setSnackbar(null)}
          >
            <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
          </Snackbar>
        )}
      </Box>
    );
  }
);

Contributions.displayName = 'Contributions';
export default Contributions;